import React, { useRef, useImperativeHandle, forwardRef, useCallback, useState } from "react";

export type WaveformCanvasHandle = {
  setupFromMediaStream: (stream: MediaStream) => void;
  setupFromSpeakerDestination: (speakerDestination: any) => void;
  teardown: () => void;
};

interface WaveformCanvasProps {
  width?: number;
  height?: number;
  lineColor?: string;
  bgColor?: string;
  style?: React.CSSProperties;
  isBotSpeaking?: boolean;
}

const WaveformCanvas = forwardRef<WaveformCanvasHandle, WaveformCanvasProps>(
  ({ width = 300, height = 100, lineColor = "rgb(63 131 248)", bgColor = "rgb(255 255 255 0)", style, isBotSpeaking = false }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const sourceRef = useRef<AudioNode | null>(null);
    const rafRef = useRef<number | null>(null);
    const [isActive, setIsActive] = useState(false);

    const startDraw = useCallback(() => {
      const canvas = canvasRef.current;
      const canvasCtx = canvas?.getContext?.("2d");
      const analyser = analyserRef.current;
      if (!canvas || !canvasCtx || !analyser) return;

      const WIDTH = canvas.width;
      const HEIGHT = canvas.height;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const draw = () => {
        rafRef.current = requestAnimationFrame(draw);
        analyser.getByteFrequencyData(dataArray);
        canvasCtx.clearRect(0, 0, WIDTH, HEIGHT);
        
        const barWidth = (WIDTH / bufferLength) * 1.5;
        const centerY = HEIGHT / 2;
        let barHeight;
        let x = 0;
        
        for (let i = 0; i < bufferLength; i++) {
          barHeight = (dataArray[i] / 2) * 0.7;
          
          // Use light gray when bot is speaking, otherwise use the original blue color
          if (isBotSpeaking) {
            // Light gray color for bot speaking
            const grayIntensity = Math.min(200, barHeight + 80);
            canvasCtx.fillStyle = `rgb(${grayIntensity} ${grayIntensity} ${grayIntensity})`;
          } else {
            // Create color based on bar height (taller bars are brighter)
            const colorIntensity = Math.min(255, barHeight + 100);
            canvasCtx.fillStyle = `rgb(${colorIntensity} 131 248)`;
          }
          
          // Draw bar centered on y-axis, extending both up and down
          canvasCtx.fillRect(x, centerY - barHeight, barWidth, barHeight * 2);
          
          x += barWidth + 1; // Add 1 pixel gap between bars
        }
      };

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      draw();
    }, [bgColor, lineColor, isBotSpeaking]);

    const teardown = useCallback(() => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      try {
        if (sourceRef.current) {
          try { sourceRef.current.disconnect(); } catch (_) {}
          sourceRef.current = null;
        }
        if (analyserRef.current) {
          try { analyserRef.current.disconnect(); } catch (_) {}
          analyserRef.current = null;
        }
      } catch (_) {}
      setIsActive(false);
      // Keep AudioContext for reuse to avoid resume issues
    }, []);

    const ensureAudioContext = useCallback(() => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const audioCtx = audioCtxRef.current;
      if (audioCtx.state === "suspended") {
        try { audioCtx.resume(); } catch (_) {}
      }
      return audioCtx;
    }, []);

    const setupFromMediaStream = useCallback((stream: MediaStream) => {
      try {
        const audioCtx = ensureAudioContext();
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;
        sourceRef.current = source;
        setIsActive(true);
        startDraw();
      } catch (e) {
        console.warn("WaveformCanvas: Failed to set up from MediaStream", e);
      }
    }, [ensureAudioContext, startDraw]);

    const setupFromSpeakerDestination = useCallback((speakerDestination: any) => {
      try {
        // Try to obtain the underlying HTMLAudioElement used by the SDK
        const audioEl: HTMLAudioElement | null =
          speakerDestination?.audioElement ||
          speakerDestination?.audio ||
          speakerDestination?.privAudio ||
          null;

        if (!audioEl) {
          console.warn("WaveformCanvas: No audio element found on SpeakerAudioDestination");
          return;
        }

        const audioCtx = ensureAudioContext();
        let source: AudioNode | null = null;
        try {
          source = audioCtx.createMediaElementSource(audioEl);
        } catch (err) {
          if (typeof (audioEl as any).captureStream === "function") {
            const stream = (audioEl as any).captureStream();
            source = audioCtx.createMediaStreamSource(stream);
          } else {
            console.warn("WaveformCanvas: Neither createMediaElementSource nor captureStream available.");
            return;
          }
        }

        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyser.connect(audioCtx.destination); // maintain playback

        analyserRef.current = analyser;
        sourceRef.current = source;
        setIsActive(true);
        startDraw();
      } catch (e) {
        console.warn("WaveformCanvas: Failed to set up from SpeakerDestination", e);
      }
    }, [ensureAudioContext, startDraw]);

    useImperativeHandle(ref, () => ({
      setupFromMediaStream,
      setupFromSpeakerDestination,
      teardown,
    }));

    return (
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className={isActive ? "waveform-active" : ""}
        style={style || { width, height, display: "block" }}
      />
    );
  }
);

export default WaveformCanvas;


